/**
 * Your initials, twelve ways.
 *
 * For anyone who would rather not put a photograph up but does not want the
 * flat grey default either. Every one of these is YOUR monogram — the same
 * two letters the portal already shows — dressed differently: solid brand
 * colours, two gradients, a ring, an outline, a split ground, a serif and a
 * typewriter face. Personal without being a picture of you.
 *
 * Drawn here as SVG, so there is no image to store, license or download.
 *
 * One constraint worth knowing: an SVG used as an `<img src>` is an isolated
 * document — it cannot reach the page's webfonts. So the faces below are
 * generic families the system always has (serif, sans-serif, monospace);
 * naming Fraunces here would silently fall back anyway.
 */

export interface AvatarStyle {
  id: string;
  label: string;
  /** The full SVG for these initials, 96×96. */
  svg: (initials: string) => string;
}

const CORAL = '#c5613f';
const CORAL_LIGHT = '#dd8868';
const NAVY = '#2f2d39';
const INK = '#242229';
const GREEN = '#3f8062';
const PLUM = '#6b4a5b';
const SAND = '#a8804e';
const PAPER = '#f6f1ea';

const SERIF = "Georgia,'Times New Roman',serif";
const SANS = "-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const MONO = "'Courier New',ui-monospace,monospace";

const open = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="96" height="96">';

/** The letters, centred. `dominant-baseline` is unreliable across renderers,
 *  so the baseline is placed by hand instead. */
const text = (s: string, o: { fill: string; font: string; size?: number; weight?: number; track?: number }) =>
  `<text x="48" y="${48 + (o.size ?? 38) * 0.35}" text-anchor="middle" fill="${o.fill}"` +
  ` font-family="${o.font}" font-size="${o.size ?? 38}" font-weight="${o.weight ?? 600}"` +
  ` letter-spacing="${o.track ?? 0}">${s}</text>`;

const solid = (id: string, label: string, ground: string, ink: string, font: string, weight = 600): AvatarStyle => ({
  id, label,
  svg: (i) => `${open}<rect width="96" height="96" fill="${ground}"/>${text(i, { fill: ink, font, weight })}</svg>`,
});

export const AVATAR_STYLES: AvatarStyle[] = [
  solid('coral', 'Coral', CORAL, '#fff', SERIF),
  solid('navy', 'Navy', NAVY, '#fff', SERIF),
  solid('green', 'Green', GREEN, '#fff', SERIF),
  solid('plum', 'Plum', PLUM, '#fff', SERIF),
  solid('sand', 'Sand', SAND, '#fff', SERIF),
  {
    id: 'paper', label: 'Paper',
    svg: (i) => `${open}<rect width="96" height="96" fill="${PAPER}"/>` +
      `<circle cx="48" cy="48" r="38" fill="none" stroke="${CORAL}" stroke-width="2"/>` +
      `${text(i, { fill: CORAL, font: SERIF })}</svg>`,
  },
  {
    id: 'warm', label: 'Warm gradient',
    svg: (i) => `${open}<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
      `<stop offset="0" stop-color="${CORAL_LIGHT}"/><stop offset="1" stop-color="${CORAL}"/></linearGradient></defs>` +
      `<rect width="96" height="96" fill="url(#g)"/>${text(i, { fill: '#fff', font: SANS, weight: 700 })}</svg>`,
  },
  {
    id: 'dusk', label: 'Dusk gradient',
    svg: (i) => `${open}<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
      `<stop offset="0" stop-color="${PLUM}"/><stop offset="1" stop-color="${NAVY}"/></linearGradient></defs>` +
      `<rect width="96" height="96" fill="url(#g)"/>${text(i, { fill: '#fff', font: SANS, weight: 700 })}</svg>`,
  },
  {
    id: 'split', label: 'Split',
    svg: (i) => `${open}<rect width="96" height="96" fill="${CORAL}"/>` +
      `<path d="M96 0v96H0Z" fill="${NAVY}"/>${text(i, { fill: '#fff', font: SANS, weight: 700 })}</svg>`,
  },
  {
    id: 'outline', label: 'Outline',
    svg: (i) => `${open}<rect width="96" height="96" fill="${INK}"/>` +
      `${text(i, { fill: 'none', font: SANS, weight: 700 })
        .replace('fill="none"', `fill="none" stroke="${CORAL}" stroke-width="1.4"`)}</svg>`,
  },
  {
    id: 'mono', label: 'Typewriter',
    svg: (i) => `${open}<rect width="96" height="96" fill="${INK}"/>` +
      `${text(i, { fill: CORAL_LIGHT, font: MONO, size: 32, weight: 700, track: 1 })}</svg>`,
  },
  {
    id: 'rings', label: 'Rings',
    svg: (i) => `${open}<rect width="96" height="96" fill="${CORAL}"/>` +
      `<circle cx="48" cy="48" r="40" fill="none" stroke="#fff" stroke-opacity=".35" stroke-width="1.5"/>` +
      `<circle cx="48" cy="48" r="33" fill="none" stroke="#fff" stroke-opacity=".22" stroke-width="1.5"/>` +
      `${text(i, { fill: '#fff', font: SERIF })}</svg>`,
  },
];

/** Two letters, the same ones the portal shows everywhere else. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((w) => w[0]).join('').toUpperCase();
  return letters || '·';
}

/** The avatar as an `<img src>` — the same shape the photo upload produces,
 *  so picking one goes through the same storage as a photograph. Encoded
 *  rather than base64'd: shorter, and readable in the database. */
export function avatarUrl(style: AvatarStyle, initials: string): string {
  return `data:image/svg+xml,${encodeURIComponent(style.svg(initials))}`;
}
